// RBAC matrix as data (NFR-3), transcribed verbatim from docs/prd/06-roles-permissions.md.
//
// Reconciliation (documented): doc 06's matrix is keyed by 8 *conceptual* roles
// (capabilities). The real Users table stores 5 *role values*. We keep the matrix keyed by
// capability (so it is auditable line-by-line against the doc) and bind it to the real roles
// via ROLE_CAPABILITIES. `can()` expands a user's roles → capabilities (+ mini-team grant),
// then evaluates the matrix + predicate + domain scope. Roles stack (union).
import type { Domain, Role } from "./enums.js";

// ── Capabilities = doc 06's conceptual roles ─────────────────────────────────
export const CAPABILITIES = [
  "student",
  "miniTeam",
  "ae", // Assistant Editor / Lead
  "jrQC", // Junior Quality Checker
  "srQC", // Senior Quality Checker
  "eic", // Editor-in-Chief
  "ops", // Operations Manager
  "admin",
] as const;
export type Capability = (typeof CAPABILITIES)[number];

/** Capabilities whose authority spans all domains (not domain-scoped). */
export const CROSS_DOMAIN_CAPABILITIES: ReadonlySet<Capability> = new Set(["eic", "ops", "admin"]);

// ── Actions = one literal per matrix row ─────────────────────────────────────
export const ACTIONS = [
  // Stage 1
  "browsePreprints",
  "assignPreprint",
  "editPitchDeck",
  "correctDomain",
  "setToPitchTeam",
  "advanceToPitchEditorial",
  "setToSelected", // direct AE-gated selection: To Pitch(Editorial) → Selected (July-2 FR-J4)
  "rejectFromPool", // Unclaimed (Unread) → Rejected Once; AE+ only, Softr pool reject
  // Stage 2
  "rankPreprints",
  "inputRanking", // a ranker submits their own ranking (multi-rater; averaged)
  "activatePreprint",
  "forceStatus", // July-14 FR-P6: Central/Admin override any preprint status, bypassing the guard
  "manageMiniTeam",
  "findReviewersActive",
  "findReviewersPreActivation",
  "claimEvaluateSuggestion",
  "studentRejectCandidate",
  "submitCoiCheck",
  "completeEvaluateForm",
  "editSimilarArticles",
  // Stage 3
  "qcOpenPortal",
  "qcSetReject",
  "qcSetFixable",
  "qcSetInvite",
  "qcSetInviteBackup", // mark a candidate as a same-institution/backup invite
  "qcSetDoubleCheck", // send directly to Double Check for a second opinion
  "qcSetUnchecked", // clear a QC decision back to Unchecked
  "qcJuniorInvite", // junior Invite → routed to Double Check
  "qcConfirmDoubleCheck",
  "markReviewerFixed",
  "editOwnProposal", // July-7 FR-K16: student edits their own reviewer profile to apply a fix
  "replyQcComment", // July-7 FR-K16: student replies to QC feedback on their proposal
  // Stage 4
  "openOperations", // July-8 FR-N4: the Operations portal itself — Manager/Central/Admin only
  "receiveBatchCsv",
  "sendJanewayInvites",
  "manageInvitations",
  "recordSubmittedReviews",
  "qcIncomingReview",
  "providePublishBlurb",
  "publishToPubPubBlueSky",
  "notifyAuthorsReviewers",
  // System
  "manageUsers",
  "configureSettings",
  "viewAuditTrail",
  // Post-doc-06 addition (COI ops UX): one-time reveal of the coi-token extension
  // signing secret from the admin board. Deliberately narrower than viewAuditTrail.
  "revealCoiExtensionSecret",
] as const;
export type Action = (typeof ACTIONS)[number];

export type PredicateName =
  | "ownPitchBeforePitching" // ¹ student/mini may correct own domain before pitching
  | "miniTeamOwnPreprint" // ² mini-team only for their assigned preprint
  | "opsCsvPassOnly" // ³ ops does a final QC on the CSV, not in the QC portal
  | "assignedEditor" // ⁵ the assigned editor reviews incoming reviews
  | "eicConfig"; // ⁶ EIC may adjust editorial config subject to admin policy

export type Decision = "allow" | "deny" | { conditional: PredicateName };

const A = "allow" as const;
const D = "deny" as const;
const cond = (p: PredicateName): Decision => ({ conditional: p });

// Column order helper: [student, miniTeam, ae, jrQC, srQC, eic, ops, admin]
function row(
  student: Decision,
  miniTeam: Decision,
  ae: Decision,
  jrQC: Decision,
  srQC: Decision,
  eic: Decision,
  ops: Decision,
  admin: Decision,
): Record<Capability, Decision> {
  return { student, miniTeam, ae, jrQC, srQC, eic, ops, admin };
}

// Verbatim transcription of the doc-06 tables.
export const MATRIX: Record<Action, Record<Capability, Decision>> = {
  // ── Stage 1 ──
  browsePreprints: row(A, A, A, A, A, A, A, A),
  assignPreprint: row(A, A, A, A, A, D, D, A),
  editPitchDeck: row(A, A, A, A, A, D, D, A),
  correctDomain: row(cond("ownPitchBeforePitching"), cond("ownPitchBeforePitching"), A, D, A, A, D, A),
  setToPitchTeam: row(A, A, A, A, A, D, D, A),
  advanceToPitchEditorial: row(D, D, A, D, A, A, D, A),
  // Direct selection by AE and above (July-2 FR-J4): Manager (ae/srQC), Central (eic), Admin.
  setToSelected: row(D, D, A, D, A, A, D, A),
  // SoftR Unclaimed reject (Unread → Rejected Once): same AE+ audience as setToSelected.
  rejectFromPool: row(D, D, A, D, A, A, D, A),
  // ── Stage 2 ──
  rankPreprints: row(D, D, D, D, D, A, D, A), // EIC final rank
  inputRanking: row(D, D, A, D, A, A, D, A), // multi-rater input: AE / Sr QC / EIC / Admin
  activatePreprint: row(D, D, D, D, D, A, D, A),
  // July-14 FR-P6: force-change status is Central (eic) / Admin only — the necessary admin
  // override. srQC stays denied so a Manager (ae/srQC/ops) does NOT inherit it; only Central's
  // eic capability and Admin grant it. Cross-domain (eic/admin are cross-domain capabilities).
  forceStatus: row(D, D, D, D, D, A, D, A),
  manageMiniTeam: row(D, D, A, D, A, A, D, A),
  findReviewersActive: row(A, A, A, A, A, D, D, A),
  findReviewersPreActivation: row(D, cond("miniTeamOwnPreprint"), A, D, A, A, D, A),
  claimEvaluateSuggestion: row(A, A, A, A, A, D, D, A),
  studentRejectCandidate: row(A, A, A, A, A, D, D, A),
  submitCoiCheck: row(A, A, A, A, A, D, D, A),
  completeEvaluateForm: row(A, A, A, A, A, D, D, A),
  editSimilarArticles: row(D, D, A, D, A, A, D, A),
  // ── Stage 3 ──
  qcOpenPortal: row(D, D, A, A, A, A, cond("opsCsvPassOnly"), A),
  qcSetReject: row(D, D, A, A, A, A, D, A),
  qcSetFixable: row(D, D, A, A, A, A, D, A),
  qcSetInvite: row(D, D, A, D, A, A, D, A), // junior denied direct Invite (uses qcJuniorInvite)
  qcSetInviteBackup: row(D, D, A, A, A, A, D, A), // same-institution/backup invite
  qcSetDoubleCheck: row(D, D, A, A, A, A, D, A), // send to Double Check for a second opinion
  qcSetUnchecked: row(D, D, A, D, A, A, D, A), // revert a QC decision (senior QC / AE)
  qcJuniorInvite: row(D, D, D, A, D, D, D, D), // junior-only path → Double Check
  qcConfirmDoubleCheck: row(D, D, A, D, A, A, D, A),
  markReviewerFixed: row(A, A, A, A, A, D, D, A),
  // July-7 FR-K16: same audience as markReviewerFixed — the route additionally enforces
  // "own proposals only" for plain students, exactly like the /fixed route.
  editOwnProposal: row(A, A, A, A, A, D, D, A),
  replyQcComment: row(A, A, A, A, A, D, D, A),
  // ── Stage 4 ──
  // July-8 FR-N4: the Operations portal is Manager (ae/srQC/ops) / Central (eic/srQC) / Admin.
  // Students and QC-in-training get no nav entry and a 403 on every operations route.
  openOperations: row(D, D, A, D, A, A, A, A),
  receiveBatchCsv: row(D, D, D, D, D, D, A, A),
  sendJanewayInvites: row(D, D, D, D, D, D, A, A),
  manageInvitations: row(D, D, D, D, D, D, A, A),
  recordSubmittedReviews: row(D, D, D, D, D, D, A, A),
  qcIncomingReview: row(D, D, cond("assignedEditor"), D, D, A, A, A),
  providePublishBlurb: row(D, D, A, D, D, D, D, A),
  publishToPubPubBlueSky: row(D, D, D, D, D, D, A, A),
  notifyAuthorsReviewers: row(D, D, D, D, D, D, A, A),
  // ── System ──
  manageUsers: row(D, D, D, D, D, D, D, A),
  configureSettings: row(D, D, D, D, D, cond("eicConfig"), D, A),
  viewAuditTrail: row(D, D, A, A, A, A, A, A),
  // Ops + Admin only: the HMAC secret lets its holder push Prophy sessions into GSM.
  revealCoiExtensionSecret: row(D, D, D, D, D, D, A, A),
};

// ── Bind capabilities to the real role values ────────────────────────────────
// Per work order §2.3 + doc 06: Manager ≈ AE/Lead + Senior QC + Operations;
// Central ≈ EIC + Senior QC; "Quality Checker in Training" = Junior QC.
export const ROLE_CAPABILITIES: Record<Role, readonly Capability[]> = {
  Student: ["student"],
  "Quality Checker in Training": ["jrQC"],
  Manager: ["ae", "srQC", "ops"],
  Central: ["eic", "srQC"],
  Admin: ["admin"],
};

// ── The actor + context that can() evaluates ─────────────────────────────────
export interface RbacUser {
  email: string;
  roles: readonly Role[];
  domains: readonly Domain[];
}

export interface RbacContext {
  /** Domain the action targets; omit for non-domain-scoped actions. */
  domain?: Domain | null;
  /** Per-preprint grants/relations used by predicates. */
  preprint?: {
    miniTeamEmails?: readonly string[];
    assignedEditorEmail?: string | null;
  };
  /** True while the student is still editing their own pitch (pre-`To Pitch(Team)`). */
  beforePitching?: boolean;
}

const PREDICATES: Record<PredicateName, (user: RbacUser, ctx: RbacContext) => boolean> = {
  ownPitchBeforePitching: (_user, ctx) => ctx.beforePitching === true,
  miniTeamOwnPreprint: (user, ctx) =>
    (ctx.preprint?.miniTeamEmails ?? []).map((e) => e.toLowerCase()).includes(user.email.toLowerCase()),
  opsCsvPassOnly: () => false, // ops re-checks the CSV in Operations, never the QC portal itself
  assignedEditor: (user, ctx) =>
    !!ctx.preprint?.assignedEditorEmail &&
    ctx.preprint.assignedEditorEmail.toLowerCase() === user.email.toLowerCase(),
  eicConfig: () => true,
};

/** Expand a user's roles to capabilities, adding a per-preprint mini-team grant when applicable. */
export function capabilitiesFor(user: RbacUser, ctx: RbacContext = {}): Set<Capability> {
  const caps = new Set<Capability>();
  for (const role of user.roles) {
    for (const cap of ROLE_CAPABILITIES[role] ?? []) caps.add(cap);
  }
  const miniEmails = (ctx.preprint?.miniTeamEmails ?? []).map((e) => e.toLowerCase());
  if (miniEmails.includes(user.email.toLowerCase())) caps.add("miniTeam");
  return caps;
}

function decisionGrants(decision: Decision, user: RbacUser, ctx: RbacContext): boolean {
  if (decision === "deny") return false;
  if (decision === "allow") return true;
  return PREDICATES[decision.conditional](user, ctx);
}

/**
 * Server-side authorization (NFR-3). Returns true iff *some* capability the user holds
 * grants `action` under `ctx`, respecting domain scope for domain-scoped capabilities.
 */
export function can(user: RbacUser, action: Action, ctx: RbacContext = {}): boolean {
  const caps = capabilitiesFor(user, ctx);
  const targetDomain = ctx.domain ?? null;
  for (const cap of caps) {
    const decision = MATRIX[action][cap];
    if (!decisionGrants(decision, user, ctx)) continue;
    if (CROSS_DOMAIN_CAPABILITIES.has(cap)) return true; // EIC/Ops/Admin: all domains
    if (targetDomain == null) return true; // action has no domain scope
    if (user.domains.includes(targetDomain)) return true; // domain-scoped: must match
    // otherwise this capability is out of scope; try the next one
  }
  return false;
}

/** Throws a 403-flavoured error when the action is not permitted. */
export class ForbiddenError extends Error {
  readonly httpStatus = 403;
  readonly action: Action;
  constructor(action: Action) {
    super(`Forbidden: not permitted to ${action}`);
    this.name = "ForbiddenError";
    this.action = action;
  }
}

export function assertCan(user: RbacUser, action: Action, ctx: RbacContext = {}): void {
  if (!can(user, action, ctx)) throw new ForbiddenError(action);
}
